// eslint-disable-next-line eslint-comments/disable-enable-pair
/* eslint-disable max-lines */
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { useState } from "react";
import { type UseFormReturn } from "react-hook-form";

import {
  type PostShiftReviewFormValues,
  type PostShiftReviewStep,
  QuestionTag,
  type StepAction,
  type StepActionData,
} from "../types";
import { useIsWorkplaceReviewUXUpdateEnabled } from "./useIsWorkplaceReviewUXUpdateEnabled";

interface UsePostShiftReviewNavigationProps {
  stepTypes: PostShiftReviewStep[];
  form: UseFormReturn<PostShiftReviewFormValues>;
  onSubmitRating?: (rating: number) => Promise<void>;
  onSubmitReview: (formData: PostShiftReviewFormValues) => Promise<void>;
  onSubmitReviewError: (error: unknown) => void;
  onComplete: () => void;
  closeModal: () => void;
  shiftId?: string;
}

export function usePostShiftReviewNavigation({
  stepTypes,
  form,
  onSubmitRating,
  onSubmitReview,
  onSubmitReviewError,
  onComplete,
  closeModal,
  shiftId,
}: UsePostShiftReviewNavigationProps) {
  const { isEnabled: isWriteReviewUiUpdateEnabled } = useIsWorkplaceReviewUXUpdateEnabled();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isSubmittingRatingLocally, setIsSubmittingRatingLocally] = useState(false);
  const [isSubmittingReviewLocally, setIsSubmittingReviewLocally] = useState(false);

  const currentStep: PostShiftReviewStep | undefined = stepTypes[currentIndex];
  const isLastContentStep = currentIndex === stepTypes.length - 2; // Before "end" step

  function goNext() {
    setCurrentIndex((index) => Math.min(index + 1, stepTypes.length - 1));
  }

  function goBack() {
    // Flag OFF: never go back past pick-rating (index 0 is pick-rating)
    // Flag ON: can go back to intro (index 0)
    const minIndex = isWriteReviewUiUpdateEnabled ? 0 : 1;
    setCurrentIndex((index) => Math.max(index - 1, minIndex));
  }

  function goToEnd() {
    setCurrentIndex(stepTypes.length - 1);
  }

  function updateFormFromStepData(
    stepType: PostShiftReviewStep,
    data: StepActionData
  ): Partial<PostShiftReviewFormValues> {
    const updates: Partial<PostShiftReviewFormValues> = {};

    switch (stepType) {
      case "write-review": {
        if ("reviewText" in data) {
          form.setValue("reviewText", data.reviewText, {
            shouldValidate: true,
          });
          updates.reviewText = data.reviewText;
        }

        break;
      }

      case QuestionTag.SOFTWARE: {
        if ("answer" in data) {
          form.setValue("softwareAnswer", data.answer, {
            shouldValidate: true,
          });
          updates.softwareAnswer = data.answer;
          if ("otherText" in data) {
            form.setValue("softwareOtherText", data.otherText, {
              shouldValidate: true,
            });
            updates.softwareOtherText = data.otherText;
          }
        }

        break;
      }

      case QuestionTag.SUPPLIES: {
        if ("answer" in data) {
          form.setValue("suppliesAnswer", data.answer, {
            shouldValidate: true,
          });
          updates.suppliesAnswer = data.answer;
        }

        break;
      }

      case QuestionTag.PATIENTS_PER_NURSE: {
        if ("answer" in data) {
          form.setValue("patientCountAnswer", data.answer, {
            shouldValidate: true,
          });
          updates.patientCountAnswer = data.answer;
        }

        break;
      }

      case QuestionTag.PARKING: {
        if ("parkingAnswer" in data) {
          form.setValue("parkingAnswer", data.parkingAnswer, {
            shouldValidate: true,
          });
          updates.parkingAnswer = data.parkingAnswer;
        }

        break;
      }

      case "choose-anonymity": {
        if ("isAnonymous" in data) {
          form.setValue("isAnonymous", data.isAnonymous, {
            shouldValidate: true,
          });
          updates.isAnonymous = data.isAnonymous;
        }

        break;
      }

      default: {
        break;
      }
    }

    return updates;
  }

  async function handleStepAction(action: StepAction) {
    if (currentStep === undefined) {
      return;
    }

    switch (action.type) {
      case "SUBMIT_RATING": {
        if (action.rating && onSubmitRating) {
          form.setValue("rating", action.rating, { shouldValidate: true });
          setIsSubmittingRatingLocally(true);
          try {
            await onSubmitRating(action.rating);
            logEvent(APP_V2_USER_EVENTS.POST_SHIFT_REVIEW_CONTINUE_CLICKED, {
              step: "pick-rating",
              shiftId,
            });

            if (isLastContentStep) {
              setIsSubmittingReviewLocally(true);
              try {
                await onSubmitReview(form.getValues());
                goToEnd();
              } catch (error: unknown) {
                onSubmitReviewError(error);
              } finally {
                setIsSubmittingReviewLocally(false);
              }
            } else {
              goNext();
            }
          } finally {
            setIsSubmittingRatingLocally(false);
          }
        }

        break;
      }

      case "NEXT_STEP": {
        // Update form data from step
        const updates = action.data ? updateFormFromStepData(currentStep, action.data) : {};

        // Check if we're finishing the last content step
        if (isLastContentStep) {
          // Submit review with current form values, defensively merging step updates
          const formData = { ...form.getValues(), ...updates };
          setIsSubmittingReviewLocally(true);
          try {
            await onSubmitReview(formData);
            logEvent(APP_V2_USER_EVENTS.POST_SHIFT_REVIEW_CONTINUE_CLICKED, {
              step: currentStep,
              shiftId,
            });
            goToEnd();
          } catch (error: unknown) {
            onSubmitReviewError(error);
          } finally {
            setIsSubmittingReviewLocally(false);
          }
        } else {
          logEvent(APP_V2_USER_EVENTS.POST_SHIFT_REVIEW_CONTINUE_CLICKED, {
            step: currentStep,
            shiftId,
          });
          goNext();
        }

        break;
      }

      case "BACK_STEP": {
        goBack();
        break;
      }

      case "SKIP_STEP": {
        if (isLastContentStep) {
          const formData = form.getValues();
          setIsSubmittingReviewLocally(true);
          try {
            await onSubmitReview(formData);
            logEvent(APP_V2_USER_EVENTS.POST_SHIFT_REVIEW_QUESTION_SKIPPED, {
              step: currentStep,
              shiftId,
            });
            goToEnd();
          } catch (error: unknown) {
            onSubmitReviewError(error);
          } finally {
            setIsSubmittingReviewLocally(false);
          }
        } else {
          logEvent(APP_V2_USER_EVENTS.POST_SHIFT_REVIEW_QUESTION_SKIPPED, {
            step: currentStep,
            shiftId,
          });
          goNext();
        }

        break;
      }

      case "CLOSE_FLOW": {
        if (isWriteReviewUiUpdateEnabled) {
          logEvent(APP_V2_USER_EVENTS.POST_SHIFT_REVIEW_SKIPPED, {
            step: currentStep,
            shiftId,
          });
        }

        closeModal();
        onComplete();
        break;
      }

      case "START_REVIEW": {
        logEvent(APP_V2_USER_EVENTS.POST_SHIFT_REVIEW_CONTINUE_CLICKED, {
          step: currentStep,
          shiftId,
        });
        goNext();
        break;
      }

      case "SUBMIT_REVIEW": {
        const formData = form.getValues();
        setIsSubmittingReviewLocally(true);
        try {
          await onSubmitReview(formData);
          goToEnd();
        } catch (error: unknown) {
          onSubmitReviewError(error);
        } finally {
          setIsSubmittingReviewLocally(false);
        }

        break;
      }

      default: {
        // No action needed for unrecognized action types
        break;
      }
    }
  }

  return {
    // currentStep is only undefined if stepTypes is empty, which is guarded in handleStepAction.
    // Consumers always receive a valid step because usePostShiftReviewSteps always returns at least 2 entries.
    currentStep: currentStep as PostShiftReviewStep,
    currentIndex,
    handleStepAction,
    isSubmittingRatingLocally,
    isSubmittingReviewLocally,
    isFirst: currentIndex === 0,
    isLast: currentIndex === stepTypes.length - 1,
  };
}
